import {Observable, Subject} from 'rxjs';
import type {
    SketchDefinitionCommittedEvent,
    SketchDefinitionDirtyEvent,
    SketchGroup,
} from '../../mesh/sketch/SketchGroup';

/**
 * 草图定义变更中心。
 *
 * 职责：
 * 1. 统一监听 `SketchGroup` 发出的定义脏化与提交事件；
 * 2. 为上层重建调度器提供稳定的事件流出口；
 * 3. 管理草图组的注册、注销与监听生命周期。
 */
export class SketchDefinitionChangeHub {
    /**
     * 已注册草图组映射。
     */
    private readonly groups = new Map<string, SketchGroup>();

    /**
     * dirty 事件流。
     */
    private readonly dirtySubject = new Subject<SketchDefinitionDirtyEvent>();

    /**
     * committed 事件流。
     */
    private readonly committedSubject = new Subject<SketchDefinitionCommittedEvent>();

    /**
     * 已注册监听器映射。
     */
    private readonly listenersBySketchId = new Map<string, {
        dirty: (event: SketchDefinitionDirtyEvent) => void;
        committed: (event: SketchDefinitionCommittedEvent) => void;
    }>();

    /**
     * 将 Three 草图组宽化为仅暴露自定义事件监听能力的目标。
     * 这里仅用于桥接当前自定义事件，不改变运行时行为。
     *
     * @param group 草图组对象。
     * @returns 自定义事件监听桥接对象。
     */
    private toEventTarget(group: SketchGroup): {
        addEventListener: (type: string, listener: EventListener) => void;
        removeEventListener: (type: string, listener: EventListener) => void;
    } {
        return group as unknown as {
            addEventListener: (type: string, listener: EventListener) => void;
            removeEventListener: (type: string, listener: EventListener) => void;
        };
    }

    /**
     * 注册草图组。
     *
     * @param group 草图组。
     */
    public registerGroup(group: SketchGroup): void {
        if (this.groups.has(group.uuid)) {
            return;
        }

        const dirtyListener = (event: SketchDefinitionDirtyEvent): void => {
            this.dirtySubject.next(event);
        };
        const committedListener = (event: SketchDefinitionCommittedEvent): void => {
            this.committedSubject.next(event);
        };

        const eventTarget = this.toEventTarget(group);
        eventTarget.addEventListener('sketch-definition-dirty', dirtyListener as unknown as EventListener);
        eventTarget.addEventListener('sketch-definition-committed', committedListener as unknown as EventListener);

        this.groups.set(group.uuid, group);
        this.listenersBySketchId.set(group.uuid, {
            dirty: dirtyListener,
            committed: committedListener,
        });
    }

    /**
     * 注销草图组。
     *
     * @param sketchId 草图 ID。
     */
    public unregisterGroup(sketchId: string): void {
        const group = this.groups.get(sketchId);
        const listeners = this.listenersBySketchId.get(sketchId);
        if (!group || !listeners) {
            return;
        }

        const eventTarget = this.toEventTarget(group);
        eventTarget.removeEventListener('sketch-definition-dirty', listeners.dirty as unknown as EventListener);
        eventTarget.removeEventListener('sketch-definition-committed', listeners.committed as unknown as EventListener);

        this.groups.delete(sketchId);
        this.listenersBySketchId.delete(sketchId);
    }

    /**
     * 监听草图 dirty 事件。
     *
     * @returns dirty 事件流。
     */
    public observeDirty(): Observable<SketchDefinitionDirtyEvent> {
        return this.dirtySubject.asObservable();
    }

    /**
     * 监听草图 committed 事件。
     *
     * @returns committed 事件流。
     */
    public observeCommitted(): Observable<SketchDefinitionCommittedEvent> {
        return this.committedSubject.asObservable();
    }

    /**
     * 释放资源。
     */
    public dispose(): void {
        Array.from(this.groups.keys()).forEach((sketchId) => {
            this.unregisterGroup(sketchId);
        });
        this.dirtySubject.complete();
        this.committedSubject.complete();
    }
}
